import { useState } from 'react';

import { formatPrice } from 'utils/helpers';
import { formatTimezoneSpecific } from 'utils/date';
import { TemplateList } from 'components/transactions-list';
import { useShowMore, handleMutation, useFetchByQueryId } from 'hooks';
import { EventCheckout, useLazyCheckoutInfoQuery } from 'store/api/event';

import { ExchangeDetails } from './details';
import { ExchangesListProps } from './types';
import { calculateDifferenceExchangeAndCheckoutItems } from './helpers';

const template = '1fr 2fr repeat(3, 1fr)';
const headers = [
  'Date',
  'Event Name',
  'Sale Total',
  'Return Amount',
  'Exchange Amount',
];

export const ExchangesList = ({
  data,
  loadMoreProps,
  hideShowMore,
  ...props
}: ExchangesListProps) => {
  const [selectedExchange, setSelectedExchange] = useState<EventCheckout>();
  const { visibleData, ...showMoreProps } = useShowMore(data);

  const [trigger, { ...detailsProps }] = useLazyCheckoutInfoQuery();
  handleMutation(detailsProps);

  const { setQueryId } = useFetchByQueryId<EventCheckout>({
    trigger,
    queryName: 'exchangeId',
    selectDetails: setSelectedExchange,
  });

  const rows = loadMoreProps ? data : visibleData;

  return (
    <>
      <TemplateList<EventCheckout>
        title="Exchanges"
        subtitle={
          loadMoreProps
            ? `${rows?.length ?? 0} of ${loadMoreProps.totalCount} shown`
            : undefined
        }
        headers={headers}
        data={rows}
        template={template}
        hasMinHeight={false}
        tabletTemplate={template}
        maxHeight={rows?.length ? 350 : undefined}
        onClick={(e, value) => setSelectedExchange(value)}
        listProps={hideShowMore ? undefined : (loadMoreProps ?? showMoreProps)}
        render={(exchange) => [
          formatTimezoneSpecific(
            exchange.createdAt,
            exchange?.event?.timezone?.code
          ),
          exchange?.event?.name,
          formatPrice(exchange.saleTotal),
          formatPrice(exchange?.exchangeItemsAmount),
          formatPrice(calculateDifferenceExchangeAndCheckoutItems(exchange)),
        ]}
      />

      <ExchangeDetails
        {...props}
        data={selectedExchange}
        opened={!!selectedExchange}
        onClose={() => {
          setSelectedExchange(undefined);
          setQueryId('');
        }}
      />
    </>
  );
};
